import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { learningService, type MorphologyProfile } from '../../services/learningService';
import CategoryDrawer from './CategoryDrawer';
import './LanguageFoundations.css';

/** Phonics category labels */
const PHONICS_LABELS: Record<string, string> = {
    // Consonant voicing
    l_r: 'L / R', b_p: 'B / P', d_t: 'D / T', f_v: 'F / V',
    s_z: 'S / Z', k_g: 'K / G',
    // Consonant manner/place
    v_w: 'V / W', m_n: 'M / N', b_v: 'B / V', l_w: 'L / W', b_g: 'B / G',
    // Same-sound spelling
    c_s: 'C / S', c_k: 'C / K', g_j: 'G / J',
    // Word-ending consonant
    t_s: 'T / S', d_s: 'D / S', t_k: 'T / K',
    // Vowel confusion
    i_e: 'I / E', a_e: 'A / E', o_u: 'O / U', a_u: 'A / U',
    a_i: 'A / I', a_o: 'A / O', e_o: 'E / O', i_o: 'I / O', u_e: 'U / E',
    // Visual confusion
    visual_bd: 'B / D', visual_pq: 'P / Q',
    // Spelling rules
    doubling: 'Doubling', double_consonant: 'Double Consonant',
    british_american: 'British / American',
    other: 'Other',
    // Prosody categories
    'prosody:weak_form': 'Weak Form',
    'prosody:contraction': 'Contraction',
    'prosody:elision': 'Elision',
    'prosody:linking': 'Linking',
    'prosody:assimilation': 'Assimilation',
};

/** Morpheme type display labels */
const MORPHEME_TYPE_LABELS: Record<string, string> = {
    prefix: 'Prefix',
    suffix: 'Suffix',
    root: 'Root',
};

interface PhonicsProfile {
    confusionMatrix: Record<string, { errors: number; encounters: number; accuracy: number }>;
    topWeaknesses: string[];
    totalLetterAttempts: number;
    totalLetterErrors: number;
    totalHintsUsed: number;
    overallPhonicsAccuracy: number;
}

/** Phonics commentary thresholds */
function getPhonicsCommentKey(accuracy: number, errorCount: number, hintsUsed: number): string {
    if (errorCount === 0 && hintsUsed === 0) return 'phonicsComment.perfect';
    if (accuracy >= 0.9) return 'phonicsComment.excellent';
    if (accuracy >= 0.75) return 'phonicsComment.good';
    if (accuracy >= 0.5) return 'phonicsComment.developing';
    return 'phonicsComment.needsPractice';
}

/** Morphology commentary thresholds */
function getMorphCommentKey(familiarity: number, morphemeCount: number): string {
    if (morphemeCount < 3) return 'morphComment.early';
    if (familiarity >= 0.8) return 'morphComment.strong';
    if (familiarity >= 0.5) return 'morphComment.growing';
    return 'morphComment.building';
}

/** Return the top N weakness labels for phonics (i18n-aware) */
/** Convert confusion matrix key to i18n-safe key (: → _) */
function toI18nKey(k: string): string {
    return k.replace(/:/g, '_');
}

/** Map confusion matrix key → reference page link */
const PROSODY_ANCHOR_MAP: Record<string, string> = {
    'prosody:weak_form': 'weak_forms',
    'prosody:contraction': 'contraction',
    'prosody:elision': 'elision',
    'prosody:linking': 'linking',
    'prosody:assimilation': 'assimilation',
};

function getConfusionLink(key: string): string {
    if (key.startsWith('prosody:')) {
        const anchor = PROSODY_ANCHOR_MAP[key] || key.replace('prosody:', '');
        return `/knowledge/prosody#${anchor}`;
    }
    return `/knowledge/phonics#${key}`;
}

function getWeaknessLabels(weaknesses: string[], tFn: (key: string, defaultValue: string) => string, limit = 3): string {
    return weaknesses
        .slice(0, limit)
        .map(k => tFn(`foundations.phonicsLabel.${toI18nKey(k)}`, PHONICS_LABELS[k] || k))
        .join(', ');
}

/** English category labels (always shown) */
const CATEGORY_EN: Record<string, string> = {
    negation: 'Negation', direction: 'Direction', degree: 'Degree',
    manner: 'Manner', quantity: 'Quantity', size: 'Size',
    noun_forming: 'Noun Forming', adj_forming: 'Adj. Forming',
    verb_forming: 'Verb Forming', adv_forming: 'Adv. Forming',
    senses: 'Senses', communication: 'Communication', movement: 'Movement',
    building: 'Building', mind: 'Mind', nature: 'Nature',
    society: 'Society', holding: 'Holding', standing: 'Standing',
};

const LanguageFoundations: React.FC = () => {
    const { t, i18n } = useTranslation('knowledgeGraph');
    const isEn = i18n.language === 'en';
    const [phonics, setPhonics] = useState<PhonicsProfile | null>(null);
    const [morphology, setMorphology] = useState<MorphologyProfile | null>(null);
    const [morphLoading, setMorphLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'phonics' | 'morphology'>('phonics');

    // Modal state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTitle, setDrawerTitle] = useState('');
    const [drawerDesc, setDrawerDesc] = useState<string | undefined>();
    const [drawerContent, setDrawerContent] = useState<React.ReactNode>(null);
    const [drawerUrl, setDrawerUrl] = useState<string | undefined>();

    const openDrawer = (title: string, description: string | undefined, content: React.ReactNode, learnMoreUrl?: string) => {
        setDrawerTitle(title);
        setDrawerDesc(description);
        setDrawerContent(content);
        setDrawerUrl(learnMoreUrl);
        setDrawerOpen(true);
    };

    useEffect(() => {
        learningService.getPhonicsProfile().then(setPhonics).catch(() => { /* ignore */ });
        learningService.getMorphologyProfile().then(data => {
            if (data && data.totalWordsAnalyzed > 0) {
                setMorphology(data);
            }
        }).catch(() => { /* ignore */ });
    }, []);

    const handleAnalyzeMorphology = async () => {
        setMorphLoading(true);
        try {
            const result = await learningService.analyzeMorphology();
            setMorphology(result);
        } catch {
            // silent
        } finally {
            setMorphLoading(false);
        }
    };

    const hasPhonics = phonics && phonics.totalLetterAttempts > 0;
    const hasMorphology = morphology && morphology.totalWordsAnalyzed > 0;

    if (!hasPhonics && !hasMorphology) return null;

    // Phonics: sort confusion categories by error count desc
    const phonicsEntries = hasPhonics
        ? Object.entries(phonics!.confusionMatrix)
            .filter(([, v]) => v.errors > 0)
            .sort((a, b) => b[1].errors - a[1].errors)
        : [];

    // Morphology: top morphemes sorted by word count
    const morphemeEntries = hasMorphology
        ? Object.entries(morphology!.morphemeMap)
            .sort((a, b) => b[1].wordCount - a[1].wordCount)
            .slice(0, 12)
        : [];

    const categoryEntries = hasMorphology
        ? Object.entries(morphology!.categoryMap)
            .sort((a, b) => b[1].totalWords - a[1].totalWords)
        : [];

    return (
        <div className="lf-section">
            <h2 className="lf-section__title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
                {t('foundations.title', 'Language Foundations')}
            </h2>
            <p className="lf-section__desc">{t('foundations.desc', 'Phonics accuracy and word formation familiarity based on your learning data.')}</p>

            {/* Tabs */}
            <div className="lf-tabs">
                {hasPhonics && (
                    <button
                        className={`lf-tab ${activeTab === 'phonics' ? 'lf-tab--active' : ''}`}
                        onClick={() => setActiveTab('phonics')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                            <line x1="12" y1="19" x2="12" y2="23"/>
                            <line x1="8" y1="23" x2="16" y2="23"/>
                        </svg>
                        {t('foundations.phonics', 'Phonics')}
                    </button>
                )}
                <button
                    className={`lf-tab ${activeTab === 'morphology' ? 'lf-tab--active' : ''}`}
                    onClick={() => setActiveTab('morphology')}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 7V4h16v3"/>
                        <path d="M9 20h6"/>
                        <path d="M12 4v16"/>
                    </svg>
                    {t('foundations.morphology', 'Morphology')}
                </button>
            </div>

            {/* Phonics Tab */}
            {activeTab === 'phonics' && hasPhonics && (
                <div className="lf-panel">
                    <div className="lf-stats-row">
                        <div className="lf-stat">
                            <span className="lf-stat__value lf-stat__value--primary">
                                {Math.round(Number(phonics!.overallPhonicsAccuracy) * 100)}%
                            </span>
                            <span className="lf-stat__label">{t('foundations.accuracy', 'Accuracy')}</span>
                        </div>
                        <div className="lf-stat">
                            <span className="lf-stat__value">{phonics!.totalLetterAttempts}</span>
                            <span className="lf-stat__label">{t('foundations.attempts', 'Letters')}</span>
                        </div>
                        <div className="lf-stat">
                            <span className="lf-stat__value">{phonics!.totalLetterErrors}</span>
                            <span className="lf-stat__label">{t('foundations.errors', 'Errors')}</span>
                        </div>
                        <div className="lf-stat">
                            <span className="lf-stat__value">{phonics!.totalHintsUsed}</span>
                            <span className="lf-stat__label">{t('foundations.hints', 'Hints')}</span>
                        </div>
                    </div>

                    {/* Commentary */}
                    <div className="lf-comment">
                        <p className="lf-comment__text">
                            {t(`foundations.${getPhonicsCommentKey(
                                Number(phonics!.overallPhonicsAccuracy),
                                phonics!.totalLetterErrors,
                                phonics!.totalHintsUsed,
                            )}`)}
                        </p>
                        {phonics!.topWeaknesses.length > 0 && (
                            <p className="lf-comment__detail">
                                {t('foundations.phonicsComment.weaknessNote', {
                                    pairs: getWeaknessLabels(phonics!.topWeaknesses, (k, d) => t(k, d)),
                                })}
                            </p>
                        )}
                    </div>

                    {phonicsEntries.length > 0 && (
                        <div className="lf-confusion-grid">
                            <h3 className="lf-sub-title">{t('foundations.confusionPatterns', 'Confusion Patterns')}</h3>
                            {phonicsEntries.map(([key, val]) => {
                                const pct = val.encounters > 0 ? (val.errors / val.encounters) * 100 : 0;
                                const label = t(`foundations.phonicsLabel.${toI18nKey(key)}`, PHONICS_LABELS[key] || key);
                                return (
                                    <div key={key} className="lf-confusion-row" onClick={() => openDrawer(
                                        label,
                                        t(`drawer.phonicsInfo.${toI18nKey(key)}`, t('drawer.phonicsInfo.other')),
                                        <div className="lf-drawer-phonics">
                                            <div className="lf-drawer-stat-row">
                                                <span className="lf-drawer-stat-label">{t('foundations.errors', 'Errors')}</span>
                                                <span className="lf-drawer-stat-value">{val.errors}</span>
                                            </div>
                                            <div className="lf-drawer-stat-row">
                                                <span className="lf-drawer-stat-label">{t('foundations.attempts', 'Letters')}</span>
                                                <span className="lf-drawer-stat-value">{val.encounters}</span>
                                            </div>
                                            <div className="lf-drawer-stat-row">
                                                <span className="lf-drawer-stat-label">{t('foundations.accuracy', 'Accuracy')}</span>
                                                <span className="lf-drawer-stat-value">{Math.round(val.accuracy * 100)}%</span>
                                            </div>
                                            <div className="lf-drawer-bar-section">
                                                <div className="lf-bar-track" style={{ height: 8 }}>
                                                    <div className="lf-bar-fill lf-bar-fill--error" style={{ width: `${Math.min(pct, 100)}%` }} />
                                                </div>
                                                <span className="lf-drawer-bar-label">{t('drawer.errorRate', 'Error Rate')}: {Math.round(pct)}%</span>
                                            </div>
                                        </div>,
                                        getConfusionLink(key),
                                    )} style={{ cursor: 'pointer' }}>
                                        <span className="lf-confusion-label lf-link">{label}</span>
                                        <div className="lf-bar-track">
                                            <div
                                                className="lf-bar-fill lf-bar-fill--error"
                                                style={{ width: `${Math.min(pct, 100)}%` }}
                                            />
                                        </div>
                                        <span className="lf-confusion-count">{val.errors}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Morphology Tab */}
            {activeTab === 'morphology' && (
                <div className="lf-panel">
                    {hasMorphology ? (
                        <>
                            <div className="lf-stats-row">
                                <div className="lf-stat">
                                    <span className="lf-stat__value lf-stat__value--primary">
                                        {Math.round(Number(morphology!.overallFamiliarity) * 100)}%
                                    </span>
                                    <span className="lf-stat__label">{t('foundations.familiarity', 'Familiarity')}</span>
                                </div>
                                <div className="lf-stat">
                                    <span className="lf-stat__value">{morphology!.totalWordsAnalyzed}</span>
                                    <span className="lf-stat__label">{t('foundations.wordsAnalyzed', 'Words')}</span>
                                </div>
                                <div className="lf-stat">
                                    <span className="lf-stat__value">{morphology!.wordsWithMorphemes}</span>
                                    <span className="lf-stat__label">{t('foundations.morphemeWords', 'Matched')}</span>
                                </div>
                                <div className="lf-stat">
                                    <span className="lf-stat__value">{Object.keys(morphology!.morphemeMap).length}</span>
                                    <span className="lf-stat__label">{t('foundations.morphemes', 'Morphemes')}</span>
                                </div>
                            </div>

                            {/* Commentary */}
                            <div className="lf-comment">
                                <p className="lf-comment__text">
                                    {t(`foundations.${getMorphCommentKey(
                                        Number(morphology!.overallFamiliarity),
                                        Object.keys(morphology!.morphemeMap).length,
                                    )}`)}
                                </p>
                                {morphology!.topWeaknesses.length > 0 && (
                                    <p className="lf-comment__detail">
                                        {t('foundations.morphComment.weaknessNote', {
                                            items: morphology!.topWeaknesses.slice(0, 3).map(k => {
                                                const parts = k.split(':');
                                                return parts.length > 1 ? parts[1] : k;
                                            }).join(', '),
                                        })}
                                    </p>
                                )}
                                {morphology!.topStrengths.length > 0 && (
                                    <p className="lf-comment__detail lf-comment__detail--positive">
                                        {t('foundations.morphComment.strengthNote', {
                                            items: morphology!.topStrengths.slice(0, 3).map(k => {
                                                const parts = k.split(':');
                                                return parts.length > 1 ? parts[1] : k;
                                            }).join(', '),
                                        })}
                                    </p>
                                )}
                            </div>

                            {/* Category overview */}
                            {categoryEntries.length > 0 && (
                                <div className="lf-morph-categories">
                                    <h3 className="lf-sub-title">{t('foundations.categories', 'Categories')}</h3>
                                    <div className="lf-category-chips">
                                        {categoryEntries.map(([cat, val]) => {
                                            const catLabel = isEn
                                                ? (CATEGORY_EN[cat] || cat.replace(/_/g, ' '))
                                                : `${t(`foundations.categoryLabel.${cat}`)} (${CATEGORY_EN[cat] || cat})`;
                                            const catMorphemes = hasMorphology
                                                ? Object.entries(morphology!.morphemeMap)
                                                    .filter(([, m]) => m.category === cat)
                                                    .sort((a, b) => b[1].wordCount - a[1].wordCount)
                                                : [];
                                            return (
                                                <div key={cat} className="lf-category-chip lf-category-chip--clickable" onClick={() => openDrawer(
                                                    catLabel,
                                                    t(`drawer.categoryInfo.${cat}`, ''),
                                                    <div className="lf-drawer-category">
                                                        <div className="lf-drawer-stat-row">
                                                            <span className="lf-drawer-stat-label">{t('foundations.familiarity', 'Familiarity')}</span>
                                                            <span className="lf-drawer-stat-value">{Math.round(val.familiarity * 100)}%</span>
                                                        </div>
                                                        <div className="lf-drawer-stat-row">
                                                            <span className="lf-drawer-stat-label">{t('foundations.wordsAnalyzed', 'Words')}</span>
                                                            <span className="lf-drawer-stat-value">{val.totalWords}</span>
                                                        </div>
                                                        {catMorphemes.length > 0 && (
                                                            <>
                                                                <h4 className="lf-drawer-sub-title">{t('foundations.morphemes', 'Morphemes')}</h4>
                                                                {catMorphemes.map(([key, m]) => {
                                                                    const [type, morpheme] = key.split(':');
                                                                    const mPct = Math.round(m.familiarity * 100);
                                                                    return (
                                                                        <div key={key} className="lf-morph-row">
                                                                            <span className="lf-morph-badge" data-type={type}>
                                                                                {type === 'prefix' ? `${morpheme}-` : type === 'suffix' ? `-${morpheme}` : morpheme}
                                                                            </span>
                                                                            <span className="lf-morph-type">{MORPHEME_TYPE_LABELS[type] || type}</span>
                                                                            <div className="lf-bar-track">
                                                                                <div
                                                                                    className={`lf-bar-fill ${mPct >= 70 ? 'lf-bar-fill--good' : mPct >= 40 ? 'lf-bar-fill--mid' : 'lf-bar-fill--low'}`}
                                                                                    style={{ width: `${mPct}%` }}
                                                                                />
                                                                            </div>
                                                                            <span className="lf-morph-pct">{mPct}%</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </>
                                                        )}
                                                    </div>,
                                                    `/knowledge/morphology#${cat}`,
                                                )}>
                                                    <span className="lf-category-chip__name">
                                                        {CATEGORY_EN[cat] || cat.replace(/_/g, ' ')}
                                                        {!isEn && <span className="lf-category-chip__native">{t(`foundations.categoryLabel.${cat}`)}</span>}
                                                    </span>
                                                    <span className="lf-category-chip__bar">
                                                        <span
                                                            className="lf-category-chip__fill"
                                                            style={{ width: `${Math.round(val.familiarity * 100)}%` }}
                                                        />
                                                    </span>
                                                    <span className="lf-category-chip__pct">{Math.round(val.familiarity * 100)}%</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Morpheme detail list */}
                            {morphemeEntries.length > 0 && (
                                <div className="lf-morph-list">
                                    <h3 className="lf-sub-title">{t('foundations.morphemeDetail', 'Morpheme Detail')}</h3>
                                    {morphemeEntries.map(([key, val]) => {
                                        const [type, morpheme] = key.split(':');
                                        const pct = Math.round(val.familiarity * 100);
                                        const morphLabel = type === 'prefix' ? `${morpheme}-` : type === 'suffix' ? `-${morpheme}` : morpheme;
                                        return (
                                            <div key={key} className="lf-morph-row lf-morph-row--clickable" onClick={() => openDrawer(
                                                morphLabel,
                                                t(`drawer.morpheme${type.charAt(0).toUpperCase() + type.slice(1)}`, ''),
                                                <div className="lf-drawer-morpheme">
                                                    <div className="lf-drawer-stat-row">
                                                        <span className="lf-drawer-stat-label">{t('foundations.morphemes', 'Morphemes')}</span>
                                                        <span className="lf-morph-badge" data-type={type}>{morphLabel}</span>
                                                    </div>
                                                    <div className="lf-drawer-stat-row">
                                                        <span className="lf-drawer-stat-label">Type</span>
                                                        <span className="lf-drawer-stat-value">{MORPHEME_TYPE_LABELS[type] || type}</span>
                                                    </div>
                                                    <div className="lf-drawer-stat-row">
                                                        <span className="lf-drawer-stat-label">{t('foundations.familiarity', 'Familiarity')}</span>
                                                        <span className="lf-drawer-stat-value">{pct}%</span>
                                                    </div>
                                                    <div className="lf-drawer-stat-row">
                                                        <span className="lf-drawer-stat-label">{t('foundations.wordsAnalyzed', 'Words')}</span>
                                                        <span className="lf-drawer-stat-value">{val.wordCount}</span>
                                                    </div>
                                                    <div className="lf-drawer-bar-section">
                                                        <div className="lf-bar-track" style={{ height: 8 }}>
                                                            <div
                                                                className={`lf-bar-fill ${pct >= 70 ? 'lf-bar-fill--good' : pct >= 40 ? 'lf-bar-fill--mid' : 'lf-bar-fill--low'}`}
                                                                style={{ width: `${pct}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                    {val.category && (
                                                        <div className="lf-drawer-stat-row">
                                                            <span className="lf-drawer-stat-label">{t('foundations.categories', 'Categories')}</span>
                                                            <span className="lf-drawer-stat-value">{CATEGORY_EN[val.category] || val.category}</span>
                                                        </div>
                                                    )}
                                                </div>,
                                                `/knowledge/morphology#${type}-${morpheme}`,
                                            )}>
                                                <span className="lf-morph-badge lf-link" data-type={type}>{morphLabel}</span>
                                                <span className="lf-morph-type">{MORPHEME_TYPE_LABELS[type] || type}</span>
                                                <div className="lf-bar-track">
                                                    <div
                                                        className={`lf-bar-fill ${pct >= 70 ? 'lf-bar-fill--good' : pct >= 40 ? 'lf-bar-fill--mid' : 'lf-bar-fill--low'}`}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                                <span className="lf-morph-pct">{pct}%</span>
                                                <span className="lf-morph-count">{val.wordCount}w</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <button className="lf-refresh-btn" onClick={handleAnalyzeMorphology} disabled={morphLoading}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                </svg>
                                {morphLoading ? t('common:loading', 'Loading...') : t('foundations.refresh', 'Refresh Analysis')}
                            </button>
                        </>
                    ) : (
                        <div className="lf-empty">
                            <p>{t('foundations.morphEmpty', 'Analyze your vocabulary to discover word formation patterns.')}</p>
                            <button className="lf-analyze-btn" onClick={handleAnalyzeMorphology} disabled={morphLoading}>
                                {morphLoading ? t('common:loading', 'Loading...') : t('foundations.analyze', 'Analyze My Vocabulary')}
                            </button>
                        </div>
                    )}
                </div>
            )}
            <CategoryDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={drawerTitle}
                description={drawerDesc}
                learnMoreUrl={drawerUrl}
            >
                {drawerContent}
            </CategoryDrawer>
        </div>
    );
};

export default LanguageFoundations;
